import { ReactNode } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import { Badge } from "@/components/catalyst/badge";
import { formatDate } from "@/lib/Formatters/formatDate";
import { BlogAudience } from "@/gql/graphql";
import type { BlogPostRecord } from "@/app/Blogs/GraphQL/blogPostRecordQuery";
import { blogAudienceLabels } from "@/app/Blogs/Components/BlogAudienceInput";

export function BlogPostDetailsView({ post }: { post: BlogPostRecord }) {
  const postUrl = new URL(
    `/blog/${post.slug}`,
    import.meta.env.VITE_APP_URL,
  ).toString();

  return (
    <dl className="divide-y divide-zinc-100 border-y border-zinc-100 text-sm">
      <Row label="Title">
        <span className="font-medium text-zinc-900">{post.title}</span>
      </Row>
      <Row label="URL">
        <a
          href={postUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 break-all font-mono text-xs text-indigo-600 hover:underline"
        >
          /blog/{post.slug}
          <ArrowTopRightOnSquareIcon className="size-3.5 shrink-0" />
        </a>
      </Row>
      <Row label="Excerpt">
        {post.excerpt ? (
          <p className="max-w-prose leading-6 text-zinc-700">{post.excerpt}</p>
        ) : (
          <span className="text-zinc-400">No excerpt</span>
        )}
      </Row>
      <Row label="Author">
        {post.author ? (
          <span className="text-zinc-900">{post.author.name}</span>
        ) : (
          <span className="text-zinc-400">No author</span>
        )}
      </Row>
      <Row label="Audience">
        <Badge
          color={post.audience === BlogAudience.Supplier ? "blue" : "zinc"}
        >
          {blogAudienceLabels[post.audience]}
        </Badge>
      </Row>
      <Row label="Published">
        {post.published_at ? (
          <span className="text-zinc-900">{formatDate(post.published_at)}</span>
        ) : (
          <span className="text-zinc-400">Draft</span>
        )}
      </Row>
    </dl>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 py-3 sm:grid-cols-[10rem_1fr] sm:gap-4">
      <dt className="font-medium text-zinc-500">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
